import { useEffect, useState } from 'react';
import ValidatedInput, { ValidatedState } from '../custom-input/ValidatedInput';
import { Validators } from '../../types/enums/validators.enum';
import { showToast } from '../../services/toast.service';
import { toastMessages } from '../../constants/errorMessages';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS } from '../../constants/common';
import './locationModal.scss';

export type CanisterFormValues = {
  name: string;
  active: boolean;
};

type CanisterModalProps = {
  mode: 'add' | 'edit';
  close: () => void;
  siteName: string;
  tankName: string;
  canister?: CanisterFormValues;
  code?: string;
  setSelectedId?: (id: number) => void;
  createCanister?: (values: CanisterFormValues) => Promise<number>;
  updateCanister?: (values: CanisterFormValues) => Promise<void>;
};

const CanisterModal = ({
  mode,
  close,
  siteName,
  tankName,
  canister,
  code,
  setSelectedId,
  createCanister,
  updateCanister,
}: CanisterModalProps): JSX.Element => {
  const [nameInput, setNameInput] = useState<ValidatedState>({ valid: mode === 'edit', value: canister?.name ?? '' });
  const [activeInput, setActiveInput] = useState<boolean>(canister?.active ?? true);
  const [validate, setValidate] = useState<boolean>(false);
  const [submit, setSubmit] = useState<boolean>(false);

  useEffect(() => {
    const action = async () => {
      const values: CanisterFormValues = { name: nameInput.value.trim(), active: activeInput };
      try {
        if (mode === 'add' && createCanister) {
          const id = await createCanister(values);
          setSelectedId?.(id);
          showToast.success(toastMessages.CANISTER_CREATED_SUCCESS);
        } else if (mode === 'edit' && updateCanister) {
          await updateCanister(values);
          showToast.success(toastMessages.CANISTER_UPDATE_SUCCESS);
        }
      } catch {
        showToast.error(mode === 'add' ? toastMessages.CANISTER_CREATED_FAIL : toastMessages.CANISTER_UPDATE_FAIL);
      } finally {
        setSubmit(false);
        close();
      }
    };
    if (submit) {
      action();
    }
  }, [submit]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setValidate(true);
    if (nameInput.valid) {
      setSubmit(true);
    }
  };

  return (
    <div className="location-modal">
      <form onSubmit={handleSubmit}>
        <h2>{mode === 'add' ? LABEL_CONSTANTS.ADD_CANISTER : LABEL_CONSTANTS.EDIT_CANISTER}</h2>
        <hr />
        <p>{mode === 'add' ? LABEL_CONSTANTS.ADD_CANISTER_HEADER : LABEL_CONSTANTS.EDIT_CANISTER_HEADER}</p>
        <div className="form-row">
          <label>{LABEL_CONSTANTS.SITE_NAME}:</label>
          <div className="input-container">
            <label>{siteName}</label>
          </div>
        </div>
        <div className="form-row">
          <label>{LABEL_CONSTANTS.TANK_NAME}:</label>
          <div className="input-container">
            <label>{tankName}</label>
          </div>
        </div>
        {mode === 'edit' && code && (
          <div className="form-row">
            <label>{LABEL_CONSTANTS.CANISTER_CODE}:</label>
            <div className="input-container">
              <label className="location-code-value">{code}</label>
            </div>
          </div>
        )}
        <div className="validated-input-row">
          <ValidatedInput
            useId="canister-name-input"
            type="text"
            label={LABEL_CONSTANTS.CANISTER_NAME}
            validate={validate}
            validatedStateForAutoFill={nameInput}
            setValidatedState={setNameInput}
            validators={[Validators.REQUIRED]}
          />
        </div>
        <div className="form-row">
          <label htmlFor="canister-active-checkbox">{LABEL_CONSTANTS.ACTIVE}</label>
          <div className="input-container">
            <input
              id="canister-active-checkbox"
              type="checkbox"
              defaultChecked={activeInput}
              onChange={e => setActiveInput(e.target.checked)}
            />
          </div>
        </div>
        <div className="flex-right submit-button location-modal-actions">
          <button type="button" className="small green inverted button" onClick={close}>
            {BUTTON_CONSTANTS.CANCEL}
          </button>
          &emsp;
          <button type="submit" className={nameInput.valid ? 'small green button' : 'small green button disabled'}>
            {mode === 'add' ? BUTTON_CONSTANTS.SUBMIT : BUTTON_CONSTANTS.UPDATE}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CanisterModal;
